<template>
    <div class="web-apps-list-page">
        <AppBreadCrumb :paths="[{ title: $t('webAppsPage.breadcrumbTitle') }]" />

        <div class="page-header">
            <div class="header-content">
                <h2>{{ $t("webAppsPage.listPageTitle") }}</h2>
                <p>{{ $t("webAppsPage.listPageSubtitle") }}</p>
            </div>
        </div>

        <a-spin :spinning="loading">
            <div class="webapp-cards p-2">
                <div v-for="(app, index) in webApps" :key="app._id" class="webapp-card" :class="{ disabled: !app.enabled }">
                    <div class="app-tags">
                        <span v-if="app.isPublic" class="app-tag public">{{ $t("webAppsPage.tagPublic") }}</span>
                        <span v-else class="app-tag private">{{ $t("webAppsPage.tagPrivate") }}</span>
                        <span v-if="!app.enabled" class="app-tag disabled-tag">{{ $t("webAppsPage.tagDisabled") }}</span>
                        <span v-if="getInstalledCount(app._id) > 0" class="app-tag installed-tag">
                            {{ $t("webAppsPage.installedCount", { count: getInstalledCount(app._id) }) }}
                        </span>
                    </div>
                    <div class="card-header">
                        <div class="app-icon-container">
                            <div class="app-icon" :style="{ background: getGradient(index) }">
                                <span class="material-icons">{{ app.icon || "web" }}</span>
                            </div>
                        </div>
                        <div class="app-info">
                            <a-tooltip :title="app.name" placement="topLeft">
                                <div class="app-title-row">
                                    <h3>{{ app.name }}</h3>
                                </div>
                            </a-tooltip>
                            <a-tooltip
                                :title="app.description || $t('webAppsPage.defaultAppDescription')"
                                placement="topLeft"
                            >
                                <div class="app-description-container">
                                    <p class="app-description" :class="{ expanded: isExpanded(app._id) }">
                                        {{ app.description || $t("webAppsPage.defaultAppDescription") }}
                                    </p>
                                    <a v-if="app.description && app.description.length > 80"
                                       @click="toggleDescription(app._id)"
                                       class="read-more-btn">
                                        {{ isExpanded(app._id) ? $t("webAppsPage.readLess") : $t("webAppsPage.readMore") }}
                                    </a>
                                </div>
                            </a-tooltip>
                        </div>
                    </div>

                    <div class="app-details">
                        <div class="detail-item" v-if="isSupsisSite || !app.isPublic">
                            <span class="material-icons">link</span>
                            <span class="detail-text">{{
                                $t("webAppsPage.webappDomainPattern", { slug: app.slug })
                            }}</span>
                        </div>
                        <div class="detail-footer">
                            <div class="detail-item">
                                <span class="material-icons">info_outline</span>
                                <span class="detail-text">{{
                                    $t("webAppsPage.versionWithPrefix", { version: app.version })
                                }}</span>
                            </div>
                            <div v-if="app.metadata && app.metadata.fileCount" class="detail-item">
                                <span class="material-icons">inventory_2</span>
                                <span class="detail-text">{{
                                    $t("webAppsPage.fileCount", { count: app.metadata.fileCount })
                                }}</span>
                            </div>
                            <div v-if="app.updatedAt" class="detail-item">
                                <span class="material-icons">schedule</span>
                                <span class="detail-text">{{ formatDate(app.updatedAt) }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="app-actions">
                        <a-button type="primary" @click="installApp(app)" :disabled="!app.enabled" class="btn-install">
                            <span class="material-icons">add_circle</span>
                            {{ $t("webAppsPage.install") }}
                        </a-button>
                        <a-button @click="openApp(app)" :disabled="!app.enabled" class="btn-open">
                            <span class="material-icons">launch</span>
                            {{ $t("webAppsPage.open") }}
                        </a-button>
                        <template v-if="isSupsisSite || !app.isPublic">
                            <a-button @click="editApp(app)" class="btn-edit">
                                <span class="material-icons">settings</span>
                            </a-button>
                            <a-button type="danger" ghost @click="confirmDelete(app)" class="btn-delete">
                                <span class="material-icons">delete_outline</span>
                            </a-button>
                        </template>
                    </div>
                </div>

                <div class="supsis-code-card" @click="openCodeSupsis">
                    <div class="code-icon">
                        <span class="material-icons">code</span>
                    </div>
                    <h3>{{ $t("webAppsPage.supsisCodeTitle") }}</h3>
                    <p>{{ $t("webAppsPage.supsisCodeDescription") }}</p>
                    <div class="cta-button">
                        <span class="material-icons">open_in_new</span>
                        {{ $t("webAppsPage.supsisCodeCta") }}
                    </div>
                </div>

                <div class="new-app-card" @click="$router.push('/integrations/webapps/new')">
                    <span class="material-icons">add</span>
                    <div>{{ $t("webAppsPage.newWebAppCard") }}</div>
                </div>
            </div>
        </a-spin>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import AppBreadCrumb from "../../../components/common/AppBreadCrumb.vue";

const SUPSIS_SITE_ID = "5d14ee322d95ad001ebdf213";

export default {
    name: "WebAppsList",
    components: {
        AppBreadCrumb,
    },
    data() {
        return {
            loading: false,
            expandedApps: [],
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            webApps: (state) => state.webapps.webApps,
            webTabs: (state) => state.webtabs.webTabs,
        }),
        isSupsisSite() {
            return this.site && this.site._id === SUPSIS_SITE_ID;
        },
    },
    methods: {
        ...mapActions({
            fetchWebApps: "webapps/fetchWebApps",
            deleteWebApp: "webapps/deleteWebApp",
            fetchWebTabs: "webtabs/fetchWebTabs",
        }),
        getInstalledCount(appId) {
            if (!this.webTabs) return 0;
            return this.webTabs.filter((tab) => tab.type === "app" && tab.appId === appId).length;
        },
        installApp(app) {
            this.$router.push({
                path: "/integrations/webtabs/new",
                query: { fromApp: app._id },
            });
        },
        getGradient(index) {
            const gradients = [
                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
                "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
                "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
                "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
                "linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)",
            ];
            return gradients[index % gradients.length];
        },
        async loadWebApps() {
            this.loading = true;
            try {
                await this.fetchWebApps();
            } catch (error) {
                this.$message.error("Web Apps yüklenirken bir hata oluştu");
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        openApp(app) {
            const url = `https://${app.slug}.webapp.supsis.ai`;
            window.open(url, "_blank");
        },
        editApp(app) {
            this.$router.push(`/integrations/webapps/${app._id}/edit`);
        },
        openCodeSupsis() {
            window.open("https://code.supsis.ai", "_blank");
        },
        confirmDelete(app) {
            const self = this;
            this.$confirm({
                title: "Web App'i silmek istediğinize emin misiniz?",
                content: `"${app.name}" (${app.slug}) silinecek. Bu işlem S3'teki tüm dosyaları da silecektir ve geri alınamaz!`,
                okText: "Sil",
                okType: "danger",
                cancelText: "İptal",
                async onOk() {
                    try {
                        await self.deleteWebApp(app._id);
                        self.$message.success("Web App silindi");
                    } catch (error) {
                        self.$message.error("Web App silinirken bir hata oluştu: " + error.message);
                        console.error(error);
                    }
                },
            });
        },
        toggleDescription(appId) {
            const index = this.expandedApps.indexOf(appId);
            if (index > -1) {
                this.expandedApps.splice(index, 1);
            } else {
                this.expandedApps.push(appId);
            }
        },
        isExpanded(appId) {
            return this.expandedApps.includes(appId);
        },
        formatDate(date) {
            if (!date) return '';
            return this.$moment(date).format('DD.MM.YYYY HH:mm');
        },
    },
    mounted() {
        this.loadWebApps();
        this.fetchWebTabs().catch((err) => {
            console.error("Failed to fetch web tabs:", err);
        });
    },
};
</script>

<style lang="scss" scoped>
.web-apps-list-page {
    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 32px;
        padding: 12px 8px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);

        .header-content {
            h2 {
                margin: 0;
                font-size: 28px;
                font-weight: 800;
                color: #1a1a1a;
                letter-spacing: -0.5px;
            }

            p {
                margin: 4px 0 0 0;
                color: #718096;
                font-size: 15px;
            }
        }

        .new-app-btn {
            height: 48px;
            padding: 0 24px;
            border-radius: 14px;
            font-weight: 700;
            display: flex;
            align-items: center;
            gap: 8px;
            box-shadow: 0 10px 20px rgba(24, 144, 255, 0.2);
            transition: all 0.3s ease;

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 15px 25px rgba(24, 144, 255, 0.3);
            }

            .material-icons {
                font-size: 22px;
            }
        }
    }

    .webapp-cards {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
        gap: 30px;

        .webapp-card {
            background: #ffffff;
            border-radius: 20px;
            padding: 24px;
            border: 1px solid rgba(0, 0, 0, 0.06);
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            flex-direction: column;
            gap: 20px;
            position: relative;

            &:hover {
                transform: translateY(-5px);
                box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
                border-color: rgba(0, 0, 0, 0.1);
            }

            .app-tags {
                position: absolute;
                top: 16px;
                right: 16px;
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 4px;
                align-items: flex-start;
                justify-content: flex-end;
                z-index: 1;
                max-width: 140px;

                .app-tag {
                    font-size: 10px;
                    font-weight: 800;
                    padding: 2px 8px;
                    border-radius: 100px;
                    text-transform: uppercase;
                    white-space: nowrap;

                    &.public { background: #e6fffa; color: #319795; }
                    &.private { background: #ebf8ff; color: #3182ce; }
                    &.disabled-tag { background: #fff5f5; color: #e53e3e; }
                    &.installed-tag { background: #f0fff4; color: #38a169; }
                }
            }

            .card-header {
                display: flex;
                gap: 16px;
                align-items: flex-start;
                background: transparent !important;

                .app-icon-container {
                    flex-shrink: 0;
                    .app-icon {
                        width: 64px;
                        height: 64px;
                        border-radius: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                        color: white;
                        box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.2);

                    .material-icons {
                        font-size: 32px;
                    }
                }
                }

                .app-info {
                    flex: 1;
                    min-width: 0;
                    
                    display: flex;
                    flex-direction: column;
                    
                    > .ant-tooltip-open {
                        display: block;
                        width: 100%;
                    }

                    .app-title-row {
                        margin-bottom: 4px;
                        width: 100%;

                        h3 {
                            font-size: 18px;
                            font-weight: 700;
                            color: #1a1a1a;
                            margin: 0;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            width: 100%;
                        }
                    }

                    .app-description-container {
                        width: 100%;

                        .app-description {
                            font-size: 14px;
                            color: #666;
                            margin: 0;
                            line-height: 1.5;
                            display: -webkit-box;
                            -webkit-line-clamp: 2;
                            line-clamp: 2;
                            -webkit-box-orient: vertical;
                            overflow: hidden;
                            transition: all 0.3s ease;
                            width: 100%;

                            &.expanded {
                                -webkit-line-clamp: unset;
                                line-clamp: unset;
                                display: block;
                                overflow: visible;
                            }
                        }

                        .read-more-btn {
                            font-size: 12px;
                            font-weight: 600;
                            color: #1890ff;
                            cursor: pointer;
                            margin-top: 4px;
                            display: inline-block;

                            &:hover {
                                color: #40a9ff;
                                text-decoration: underline;
                            }
                        }
                    }
                }
            }

            .app-details {
                background: rgba(0, 0, 0, 0.02);
                padding: 12px 16px;
                border-radius: 12px;
                display: flex;
                flex-direction: column;
                gap: 8px;

                .detail-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #888;

                    .material-icons {
                        font-size: 16px;
                    }

                    .detail-text {
                        font-size: 12px;
                        font-weight: 500;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                }

                .detail-footer {
                    display: flex;
                    justify-content: space-between;
                    border-top: 1px solid rgba(0, 0, 0, 0.05);
                    padding-top: 8px;
                    margin-top: 4px;
                }
            }

            .app-actions {
                display: flex;
                gap: 8px;
                align-items: center;

                .btn-install {
                    flex: 1;
                    height: 38px;
                    border-radius: 10px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    background: linear-gradient(135deg, #10b981, #059669);
                    border: none;

                    .material-icons { font-size: 16px; }
                    &:hover { background: linear-gradient(135deg, #059669, #047857); }
                }

                .btn-open {
                    height: 38px;
                    border-radius: 10px;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                    border-color: #eee;
                    color: #555;
                    background: white;

                    .material-icons { font-size: 16px; }
                    &:hover { color: #1890ff; border-color: #1890ff; }
                }

                .btn-edit {
                    width: 38px;
                    height: 38px;
                    flex-shrink: 0;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                    border-color: #eee;
                    color: #555;

                    .material-icons { font-size: 16px; }
                    &:hover { color: #1890ff; border-color: #1890ff; }
                }

                .btn-delete {
                    width: 38px;
                    height: 38px;
                    flex-shrink: 0;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;

                    .material-icons { font-size: 18px; }
                }
            }
        }

        .supsis-code-card, .new-app-card {
            background: #ffffff;
            border: 2px dashed rgba(0, 0, 0, 0.1);
            border-radius: 20px;
            padding: 30px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            cursor: pointer;
            transition: all 0.3s ease;
            min-height: 280px;

            &:hover {
                background: #fdfdfd;
                border-color: #1890ff;
                transform: scale(1.02);
            }
        }

        .supsis-code-card {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);

            .code-icon {
                width: 60px;
                height: 60px;
                background: #667eea;
                border-radius: 15px;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 20px;
                color: white;
                box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
                .material-icons { font-size: 32px; }
            }

            h3 { font-size: 18px; font-weight: 700; margin-bottom: 8px; color: #2d3748; }
            p { font-size: 14px; color: #718096; margin-bottom: 20px; max-width: 200px; }

            .cta-button {
                font-size: 14px;
                color: #667eea;
                font-weight: 700;
                display: flex;
                align-items: center;
                gap: 6px;

                &:hover { text-decoration: underline; }
            }
        }

        .new-app-card {
            .material-icons {
                font-size: 40px;
                color: #cbd5e0;
                margin-bottom: 16px;
            }
            div {
                font-size: 16px;
                font-weight: 600;
                color: #4a5568;
            }
        }
    }
}
</style>
